// src/services/chat.js
import api from './api';

/**
 * Non-streaming chat query (fallback)
 *
 * The third argument (a session id some callers pass) has never been sent; it is kept in place so
 * those callers' positions do not move. options.edgeNodeId (R-chat): the selected site. When set,
 * the backend limits the chat's context and every tool to that edge node; when absent the request
 * is exactly as before.
 */
export const sendChatQuery = async (message, history = [], _sessionId = undefined, options = {}) => {
  try {
    const requestData = {
      message,
      history
    };
    if (options.edgeNodeId) requestData.edge_node_id = options.edgeNodeId;

    const response = await api.post('/chat/', requestData);
    return response.data;
  } catch (error) {
    console.error('Error in chat service:', error);
    throw error;
  }
};

/**
 * Streaming chat query using Server-Sent Events via fetch().
 *
 * Uses native fetch + ReadableStream because:
 * - EventSource only supports GET (we need POST with JSON body)
 * - Axios doesn't expose ReadableStream for incremental reading
 *
 * @param {string} message - User message
 * @param {Array} history - Chat history
 * @param {Object} callbacks - { onStatus, onChunk, onDone, onError }
 */
export const sendChatQueryStreaming = async (message, history = [], callbacks = {}) => {
  const { onStatus, onChunk, onDone, onError } = callbacks;

  const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const token = localStorage.getItem('token');

  const response = await fetch(`${baseURL}/chat/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ message, history }),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    // Parse SSE events from buffer
    const lines = buffer.split('\n');
    buffer = lines.pop(); // Keep incomplete line in buffer

    let eventType = null;
    for (const line of lines) {
      if (line.startsWith('event: ')) {
        eventType = line.slice(7).trim();
      } else if (line.startsWith('data: ')) {
        const data = line.slice(6);

        switch (eventType) {
          case 'status':
            onStatus?.(data);
            break;
          case 'chunk': {
            // Chunks are JSON-encoded strings from the backend
            try {
              const text = JSON.parse(data);
              onChunk?.(text);
            } catch {
              // Fallback: use raw data if JSON parse fails
              onChunk?.(data);
            }
            break;
          }
          case 'done':
            try {
              const result = JSON.parse(data);
              onDone?.(result);
            } catch {
              onDone?.({ content: data });
            }
            break;
          case 'error':
            try {
              const err = JSON.parse(data);
              onError?.(err);
            } catch {
              onError?.({ error: data });
            }
            break;
          default:
            break;
        }
        eventType = null;
      }
    }
  }
};

// Export as service object
const chatService = {
  sendChatQuery,
  sendChatQueryStreaming
};

export default chatService;
